import React from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import PermissionWrapper from '../../components/Permissions/PermissionWrapper';
import HeaderBar from '../../components/HeaderBar';
import FooterBar from '../../components/FooterBar';
import { isCurrentUserDemo, isDemoFeatureAllowed } from '../../utils/demoUserConfig';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0;
`;

const AppsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  column-gap: 28px;
  row-gap: 20px;
  margin-bottom: 20px;
  width: 100%;
  padding: 0 16px;
`;

const AppWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
`;

const AppIcon = styled.div`
  width: 64px;
  height: 64px;
  background-color: ${props => props.color};
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
  
  &:active {
    transform: scale(0.95);
  }
  
  &:hover {
    transform: scale(1.05);
  }
`;

const AppLabel = styled.div`
  margin-top: 8px;
  font-size: 10px;
  color: white;
  text-align: center;
  line-height: 1.2;
  max-width: 70px;
`;



const AllApps = () => {
  const navigate = useNavigate();
  const isDemo = isCurrentUserDemo();

  // Define all apps (same as in landing page)
  const allApps = [
    {
      id: 'workflows',
      name: 'Workflows',
      color: '#FF3B30',
      onClick: () => navigate('/flows')
    },
    {
      id: 'bookings',
      name: 'Bookings',
      color: '#FFFF00',
      onClick: () => navigate('/bookings')
    },
    {
      id: 'catalog',
      name: 'Catalog', 
      color: '#17A2B8',
      onClick: () => navigate('/catalog')
    },
    {
      id: 'tourmngr',
      name: 'TourMngr',
      color: '#FF9500',
      onClick: () => navigate('/tour-manager/view-all')
    },
    {
      id: 'notes',
      name: 'Notes',
      color: '#7C4DFF',
      onClick: () => navigate('/notes')
    },
    {
      id: 'connectors',
      name: 'Connectors',
      color: '#4285F4',
      onClick: () => navigate('/connectors')
    },
    {
      id: 'analytics',
      name: 'Analytics',
      color: '#34C759',
      onClick: () => navigate('/analytics')
    },
    {
      id: 'tasks',
      name: 'Tasks',
      color: '#FF6B35',
      onClick: () => navigate('/tasks')
    },
    /* Temporarily hidden - not ready for MVP
    {
      id: 'merch',
      name: 'Merch',
      color: '#9932CC', 
      onClick: () => navigate('/merch')
    },
    {
      id: 'charting',
      name: 'Charting',
      color: '#FF9500',
      onClick: () => navigate('/charting')
    },
    {
      id: 'playlisting',
      name: 'Playlisting',
      color: '#5856D6',
      onClick: () => navigate('/playlisting')
    },
    {
      id: 'social-listening',
      name: 'Social Listening',
      color: '#007AFF',
      onClick: () => navigate('/social-listening')
    },
    */
    // Spotify Revenue app temporarily hidden - uses mock data
    // {
    //   id: 'spotify-revenue',
    //   name: 'Spotify Revenue',
    //   color: '#1DB954',
    //   onClick: () => navigate('/spotify-revenue')
    // },
    {
      id: 'knowledge',
      name: 'Knowledge',
      color: '#5856D6',
      onClick: () => navigate('/knowledge-base')
    },
    {
      id: 'contacts',
      name: 'Contacts',
      color: '#AF52DE',
      onClick: () => navigate('/contacts')
    },
    {
      id: 'team-management',
      name: 'Teams',
      color: '#8B5CF6',
      onClick: () => navigate('/team-management')
    },
    /* Email app temporarily hidden - not ready for MVP
    {
      id: 'email',
      name: 'Email',
      color: '#F5F5F5',
      onClick: () => navigate('/email')
    }
    */
  ];

  // Filter apps for demo users
  const apps = isDemo 
    ? allApps.filter(app => {
        // Hide these apps for demo users
        const hiddenApps = ['workflows', 'bookings', 'catalog', 'tourmngr', 'contacts', 'team-management', 'analytics'];
        return !hiddenApps.includes(app.id);
      })
    : allApps;

  const handleBack = () => {
    navigate('/');
  };

  return (
    <div className='h-auto bg-black'>
      {/* Fixed Header */}
      <div className="fixed top-0 w-full flex justify-center bg-black z-[25] border border-black">
        <div style={{ display: 'flex', width: '358px' }}>
          <HeaderBar isHome={false} />
        </div>
      </div>

      {/* Main Content */}
      <div className='relative w-full text-[12px] pt-[80px] h-auto flex justify-center items-start bg-[black]'>
        <div className="w-[390px] h-[80vh] flex flex-col items-center relative z-[0]">
          
          {/* Header */}
          <div className="px-4 my-[50px] flex w-full justify-between">
            <span className="text-white text-[14px]" style={{ fontWeight: 300 }}>All Apps</span>
            <span 
              className="text-white text-[12px] font-normal cursor-pointer hover:opacity-70" 
              onClick={handleBack}
            >
              Go back
            </span>
          </div>

          <Container>
            <AppsGrid>
              {apps.map((app) => (
                <PermissionWrapper key={app.id} appName={app.id}>
                  <AppWrapper>
                    <AppIcon
                      className={`${app.id}-app-icon`}
                      color={app.color}
                      onClick={app.onClick}
                    />
                    <AppLabel>{app.name}</AppLabel>
                  </AppWrapper>
                </PermissionWrapper>
              ))}
            </AppsGrid>
          </Container>
        </div>
      </div>

      {/* Home Footer */}
      <FooterBar isHome={true} />
    </div>
  );
};

export default AllApps; 